import { useEffect, useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { User, XIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useErrorHandler } from "../../hooks/useErrorHandler";
import { useSubscriptions } from "../../hooks/useSubscriptions";
import { api } from "../../utils/api";
import {
  getDetailedSubscriptionInfo,
  hasRevenueCatEntitlements,
} from "../../utils/revenuecat";
import { ContactSupportCard } from "../managementCards/ContactSupportCard";
import { SamphireMembershipInfoCard } from "../managementCards/SamphireMembershipInfoCard";
import { useAuth } from "../providers/AuthProvider";

/**
 * SideDrawer component for web app. Users can access their account information and manage their subscription.
 */
export default function SideDrawer() {
  // Hide SideDrawer from web app since all purchase flows are unauthenticated for now
  return null;

  // Original code for authenticated SideDrawer
  const [open, setOpen] = useState(false);
  const [backdropVisible, setBackdropVisible] = useState(false);
  const { user, signOut, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { handleError } = useErrorHandler({
    fallbackMessage: "Failed to load account data. Please try again.",
  });

  // State for subscription data
  const [hasEntitlements, setHasEntitlements] = useState(false);
  const [detailedSubscription, setDetailedSubscription] = useState<any>(null);

  // State for redeem token functionality
  const [showRedeemForm, setShowRedeemForm] = useState(false);
  const [redeemTokenInput, setRedeemTokenInput] = useState("");
  const [redeemTokenError, setRedeemTokenError] = useState<string | null>(null);
  const [redeemTokenSuccess, setRedeemTokenSuccess] = useState<string | null>(
    null,
  );

  //get user email from trpc query
  const userEmail = api.user.queryUserEmail.useQuery(undefined, {
    enabled: !!user && !authLoading,
  });

  // Handle query errors using useEffect to avoid render-time side effects
  useEffect(() => {
    if (userEmail.error) {
      handleError(userEmail.error);
    }
  }, [userEmail.error, handleError]);

  // Get subscriptions data
  const { subscriptions, refetch: refetchSubscriptions } =
    useSubscriptions(user);

  // Redeem token mutations
  const redeemTokenMutation = (
    api as any
  ).redeemTokens.redeemToken.useMutation();

  // Close drawer when user logs out
  useEffect(() => {
    if (!user && open) {
      setOpen(false);
    }
  }, [user, open]);

  // Refetch queries when authentication completes
  useEffect(() => {
    if (user && !authLoading) {
      // Small delay to ensure authentication is fully established
      const timer = setTimeout(() => {
        userEmail.refetch();
      }, 100);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  // Control backdrop animation timing
  useEffect(() => {
    if (open) {
      // Small delay to ensure smooth animation
      const timer = setTimeout(() => setBackdropVisible(true), 10);
      return () => clearTimeout(timer);
    } else {
      setBackdropVisible(false);
    }
  }, [open]);

  // Function to check RevenueCat entitlements
  const checkEntitlements = async () => {
    if (!user) return;

    try {
      // Force re-initialize RevenueCat to ensure we have fresh user data
      console.log("SideDrawer: Re-initializing RevenueCat for user:", user);
      const { initializeRevenueCat } = await import("../../utils/revenuecat");
      await initializeRevenueCat(user);

      // Small delay to ensure initialization is complete
      await new Promise((resolve) => setTimeout(resolve, 500));

      const entitlements = await hasRevenueCatEntitlements();
      setHasEntitlements(entitlements);

      // Get detailed subscription info if user has entitlements
      if (entitlements) {
        const detailedSub = await getDetailedSubscriptionInfo();
        setDetailedSubscription(detailedSub);
      } else {
        setDetailedSubscription(null);
      }
    } catch (error) {
      console.error("Error checking entitlements:", error);
      setHasEntitlements(false);
      setDetailedSubscription(null);
    }
  };

  // Check RevenueCat entitlements on user change
  useEffect(() => {
    checkEntitlements();
  }, [user]);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/");
      setOpen(false); // Close the drawer after signing out
    } catch (error) {
      console.error("Sign out error:", error);
    }
  };

  // Handle redeem token submission
  const handleRedeemToken = async () => {
    if (!redeemTokenInput || redeemTokenInput.length !== 10) {
      setRedeemTokenError("Please enter a valid 10-digit token");
      return;
    }

    try {
      setRedeemTokenError(null);
      setRedeemTokenSuccess(null);

      const result = await redeemTokenMutation.mutateAsync({
        token: redeemTokenInput,
      });

      if (result.success) {
        setRedeemTokenSuccess(
          "Token redeemed successfully! Your subscription is now active.",
        );
        setRedeemTokenInput("");
        setShowRedeemForm(false);

        // Refetch subscription data to show the newly linked subscription
        await refetchSubscriptions();

        // Also refetch RevenueCat entitlements in case the token links to a RevenueCat subscription
        await checkEntitlements();
      }
    } catch (error: any) {
      console.error("Error redeeming token:", error);
      setRedeemTokenError(error.message || "Failed to redeem token");
    }
  };

  // Reset redeem form when closing
  const resetRedeemForm = () => {
    setShowRedeemForm(false);
    setRedeemTokenInput("");
    setRedeemTokenError(null);
    setRedeemTokenSuccess(null);
  };

  // Helper functions
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "Invalid date";
    }
  };

  // Get subscription info (prefer RevenueCat over database)
  const getSubscriptionInfo = () => {
    if (hasEntitlements && detailedSubscription) {
      return {
        hasSubscription: true,
        startDate: formatDate(detailedSubscription.purchaseDate),
        membershipType: (detailedSubscription.billingPeriod === "P1M"
          ? "Monthly"
          : "Annual") as "Monthly" | "Annual",
        renewalDate: formatDate(detailedSubscription.expiresAt),
      };
    } else if (subscriptions && subscriptions.length > 0) {
      const firstSub = subscriptions[0];

      return {
        hasSubscription: true,
        startDate: formatDate(
          firstSub?.createdAt || firstSub?.purchaseDate || "N/A",
        ), // Use creation date for start date
        membershipType: "Custom" as const,
        renewalDate: formatDate(firstSub?.expiresAt || "N/A"), // Keep expiration date for renewal
      };
    }
    return {
      hasSubscription: false,
      startDate: null,
      membershipType: null,
      renewalDate: null,
    };
  };

  return (
    <div>
      {user ? (
        <button
          onClick={() => setOpen(true)}
          className="flex group cursor-pointer hover:text-blue-900 text-brand-primary-darker text-xs uppercase font-medium  flex-row gap-2 items-center"
        >
          Account Management
          <div className="flex flex-row items-center gap-center px-2 py-2 rounded-full bg-blue-100 text-brand-primary-darker group-hover:bg-blue-200">
            <User className="w-4 h-4" />
          </div>
        </button>
      ) : (
        <button
          onClick={() => navigate("/auth")}
          className="flex group cursor-pointer hover:text-blue-900 text-brand-primary-darker text-xs uppercase font-medium  flex-row gap-2 items-center"
        >
          Log In
          <div className="flex flex-row items-center gap-center px-2 py-2 rounded-full bg-blue-100 text-brand-primary-darker group-hover:bg-blue-200">
            <User className="w-4 h-4" />
          </div>
        </button>
      )}
      <Dialog open={open} onClose={setOpen} className="relative z-10">
        {/* Backdrop overlay with proper blur animation */}
        <div
          className={`fixed inset-0 transition-all duration-500 ease-in-out ${
            backdropVisible
              ? "bg-gray-500/50 backdrop-blur-sm"
              : "bg-transparent backdrop-blur-none"
          }`}
        />

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
              <DialogPanel
                transition
                className="pointer-events-auto w-screen max-w-md transform transition duration-500 ease-in-out data-closed:translate-x-full sm:duration-700"
              >
                <div className="flex h-full flex-col overflow-y-auto bg-canvas-base py-6 shadow-xl">
                  <div className="px-4 sm:px-6">
                    <div className="flex items-start justify-between">
                      <DialogTitle className="text-base font-semibold text-foreground-primary-900">
                        Your Samphire Account
                      </DialogTitle>
                      <div className="ml-3 flex h-7 items-center">
                        <button
                          type="button"
                          onClick={() => setOpen(false)}
                          className="cursor-pointer relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:outline-hidden"
                        >
                          <span className="absolute -inset-2.5" />
                          <span className="sr-only">Close panel</span>
                          <XIcon aria-hidden="true" className="size-6" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="relative mt-6 flex-1 px-4 sm:px-6 flex flex-col gap-4 overflow-y-auto scrollbar-visible">
                    <SamphireMembershipInfoCard
                      email={userEmail?.data || "Not available"}
                      hasSubscription={getSubscriptionInfo().hasSubscription}
                      startDate={getSubscriptionInfo().startDate || "N/A"}
                      membershipType={
                        getSubscriptionInfo().membershipType || "N/A"
                      }
                      renewalDate={getSubscriptionInfo().renewalDate || "N/A"}
                      userID={user || "Not available"}
                    />

                    {/* Redeem Token Section */}
                    {!getSubscriptionInfo().hasSubscription && (
                      <div className="rounded-2xl bg-blue-50 border border-blue-200 p-4">
                        <div className="flex justify-between items-center mb-2">
                          <h3 className="text-sm font-semibold text-blue-900">
                            🎫 Have an Access Code?
                          </h3>
                          {!showRedeemForm && (
                            <button
                              onClick={() => setShowRedeemForm(true)}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium underline"
                            >
                              Redeem Code
                            </button>
                          )}
                        </div>

                        {!showRedeemForm ? (
                          <p className="text-sm text-blue-700">
                            If you have an access code from a purchase, enter it
                            here. You can find it in your confirmation email.
                          </p>
                        ) : (
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-blue-900 mb-1">
                                Enter your 10-digit access code:
                              </label>
                              <input
                                type="text"
                                value={redeemTokenInput}
                                onChange={(e) => {
                                  const value = e.target.value
                                    .replace(/\D/g, "")
                                    .slice(0, 10);
                                  setRedeemTokenInput(value);
                                  setRedeemTokenError(null);
                                }}
                                placeholder="1234567890"
                                className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-center tracking-widest"
                                maxLength={10}
                              />
                            </div>

                            {redeemTokenError && (
                              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
                                {redeemTokenError}
                              </div>
                            )}

                            <div className="flex gap-2">
                              <button
                                onClick={handleRedeemToken}
                                disabled={
                                  redeemTokenMutation.isPending ||
                                  redeemTokenInput.length !== 10
                                }
                                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                              >
                                {redeemTokenMutation.isPending
                                  ? "Redeeming..."
                                  : "Redeem Code"}
                              </button>
                              <button
                                onClick={resetRedeemForm}
                                className="px-4 py-2 border border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 text-sm font-medium"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Success message */}
                    {redeemTokenSuccess && (
                      <div className="rounded-2xl bg-green-50 border border-green-200 p-4">
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                            <svg
                              className="w-3 h-3 text-green-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          </div>
                          <p className="text-sm font-medium text-green-800">
                            {redeemTokenSuccess}
                          </p>
                        </div>
                      </div>
                    )}
                    <ContactSupportCard />

                    {/* Sign Out Button */}
                    <div className="mt-4 pt-4 border-t border-gray-200 flex flex-row justify-center">
                      <button
                        onClick={handleSignOut}
                        className="text-blue-600 hover:text-blue-800 underline text-sm cursor-pointer bg-transparent border-none p-0 "
                      >
                        Log out
                      </button>
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
